import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const Formularios = () => {
  const { claseId } = useParams();
  const navigate = useNavigate();
  const { user, API } = useContext(AuthContext);
  const [formularios, setFormularios] = useState([]);
  const [clase, setClase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newFormulario, setNewFormulario] = useState({
    titulo: '',
    descripcion: '',
    fecha_limite: ''
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedFormulario, setSelectedFormulario] = useState(null);
  const [activeTab, setActiveTab] = useState('lista');
  const [isGoogleDialogOpen, setIsGoogleDialogOpen] = useState(false);
  const [googleFormulario, setGoogleFormulario] = useState({
    titulo: '',
    descripcion: '',
    google_form_url: '',
    fecha_limite: ''
  });
  const [creatingGoogle, setCreatingGoogle] = useState(false);
  
  // Para editar formularios
  const [editingFormulario, setEditingFormulario] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Para crear preguntas
  const [newPregunta, setNewPregunta] = useState({
    titulo: '',
    tipo: 'opcion_multiple',
    opciones: ['', ''],
    respuesta_correcta: '',
    puntuacion: 1.0,
    orden: 1
  });

  useEffect(() => {
    if (claseId) {
      loadData();
    } else {
      navigate('/app/clases');
    }
  }, [claseId]);

  const loadData = async () => {
    try {
      const [formulariosRes, claseRes] = await Promise.all([
        axios.get(`${API}/formularios/clase/${claseId}`),
        axios.get(`${API}/clases`)
      ]);
      
      setFormularios(formulariosRes.data);
      
      const claseEncontrada = claseRes.data.find(c => c.id === claseId);
      setClase(claseEncontrada);
    } catch (error) {
      toast.error('Error al cargar formularios');
      navigate('/app/clases');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFormulario = async (e) => {
    e.preventDefault();
    if (!newFormulario.titulo || !newFormulario.descripcion) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/formularios`, {
        ...newFormulario,
        clase_id: claseId,
        fecha_limite: newFormulario.fecha_limite ? new Date(newFormulario.fecha_limite).toISOString() : null
      });
      
      setFormularios([response.data, ...formularios]);
      setNewFormulario({ titulo: '', descripcion: '', fecha_limite: '' });
      setIsDialogOpen(false);
      toast.success('Formulario creado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear formulario');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateGoogleFormulario = async (e) => {
    e.preventDefault();
    if (!googleFormulario.titulo || !googleFormulario.descripcion || !googleFormulario.google_form_url) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    // Basic URL validation
    if (!googleFormulario.google_form_url.includes('google.com') && !googleFormulario.google_form_url.includes('forms.gle')) {
      toast.error('Por favor ingresa una URL válida de Google Forms');
      return;
    }

    setCreatingGoogle(true);
    try {
      const response = await axios.post(`${API}/formularios/google-classroom`, {
        ...googleFormulario,
        clase_id: claseId,
        fecha_limite: googleFormulario.fecha_limite ? new Date(googleFormulario.fecha_limite).toISOString() : null
      });
      
      setFormularios([response.data, ...formularios]);
      setGoogleFormulario({ titulo: '', descripcion: '', google_form_url: '', fecha_limite: '' });
      setIsGoogleDialogOpen(false);
      toast.success('Formulario de Google Classroom importado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar formulario de Google Classroom');
    } finally {
      setCreatingGoogle(false);
    }
  };

  const handleCreatePregunta = async (formularioId) => {
    if (!newPregunta.titulo) {
      toast.error('El título de la pregunta es obligatorio');
      return;
    }

    try {
      await axios.post(`${API}/formularios/${formularioId}/preguntas`, newPregunta);
      
      setNewPregunta({
        titulo: '',
        tipo: 'opcion_multiple',
        opciones: ['', ''],
        respuesta_correcta: '',
        puntuacion: 1.0,
        orden: 1
      });
      
      toast.success('Pregunta agregada exitosamente');
      // Reload to show updated questions
      loadData();
    } catch (error) {
      toast.error('Error al crear pregunta');
    }
  };

  const addOpcion = () => {
    setNewPregunta({
      ...newPregunta,
      opciones: [...newPregunta.opciones, '']
    });
  };

  const removeOpcion = (index) => {
    if (newPregunta.opciones.length > 2) {
      const opciones = newPregunta.opciones.filter((_, i) => i !== index);
      setNewPregunta({ ...newPregunta, opciones });
    }
  };

  const updateOpcion = (index, value) => {
    const opciones = [...newPregunta.opciones];
    opciones[index] = value;
    setNewPregunta({ ...newPregunta, opciones });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Edit formulario functions
  const openEditDialog = (formulario) => {
    setEditingFormulario({
      titulo: formulario.titulo,
      descripcion: formulario.descripcion,
      fecha_limite: formulario.fecha_limite ? new Date(formulario.fecha_limite).toISOString().slice(0, 16) : '',
      activo: formulario.activo
    });
    setSelectedFormulario(formulario);
    setIsEditDialogOpen(true);
  };

  const handleEditFormulario = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const updateData = {
        titulo: editingFormulario.titulo,
        descripcion: editingFormulario.descripcion,
        fecha_limite: editingFormulario.fecha_limite || null,
        activo: editingFormulario.activo
      };

      const response = await axios.put(`${API}/formularios/${selectedFormulario.id}`, updateData);
      
      setFormularios(formularios.map(f => 
        f.id === selectedFormulario.id ? response.data : f
      ));
      
      setIsEditDialogOpen(false);
      setEditingFormulario(null);
      setSelectedFormulario(null);
      
      toast.success('Formulario actualizado exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar formulario');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteFormulario = async (formulario) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar el formulario "${formulario.titulo}"?`)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/formularios/${formulario.id}`);
      
      if (response.data.success) {
        // Check if formulario was deactivated (has responses) or deleted
        if (response.data.message.includes('desactivado') || response.data.message.includes('respuestas')) {
          // Formulario has responses - was deactivated
          setFormularios(formularios.map(f => 
            f.id === formulario.id ? { ...f, activo: false } : f
          ));
          toast.warning('No se puede eliminar, tiene respuestas. Formulario inactivado.');
        } else {
          // Formulario was completely deleted (no responses)
          setFormularios(formularios.filter(f => f.id !== formulario.id));
          toast.success('Formulario eliminado exitosamente');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar formulario');
    } finally {
      setDeleting(false);
    }
  };

  // Check if current user is enrolled as student in this professor class
  const isEnrolledAsStudent = () => {
    if (!clase || !user) return false;
    
    // If this is a "profesores" class and current user is a profesor
    if (clase.categoria === 'profesores' && user.rol === 'profesor') {
      // Check if current professor is enrolled as student in this class
      return clase.estudiantes_inscritos && clase.estudiantes_inscritos.includes(user.id);
    }
    
    return false;
  };

  // Check if user can edit/delete formulario
  const canEditFormulario = (formulario) => {
    // If enrolled as student in this professor class, can't edit
    if (isEnrolledAsStudent()) {
      return false;
    }
    
    return user.rol === 'admin' || 
           (user.rol === 'profesor' && formulario.profesor_id === user.id);
  };

  // Check if user can create new formularios
  const canCreateFormulario = () => {
    // If enrolled as student in this professor class, can't create
    if (isEnrolledAsStudent()) {
      return false;
    }
    
    return user.rol === 'admin' || 
           (user.rol === 'profesor' && clase && clase.profesor_id === user.id);
  };

  // Determine user's effective role in this class
  const getEffectiveRole = () => {
    if (isEnrolledAsStudent()) {
      return 'estudiante'; // Professor acts as student in this class
    }
    return user.rol; // Normal role
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!clase) {
    return (
      <div className="space-y-8">
        <Card className="shadow-lg border-0">
          <CardContent className="text-center py-12">
            <i className="fas fa-exclamation-triangle text-gray-300 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Clase no encontrada</h3>
            <p className="text-gray-600">La clase que buscas no existe o no tienes acceso.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="formularios-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/app/clases')}
            className="mb-4 text-blue-600 hover:text-blue-800"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver a Clases
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Formularios de Evaluación
          </h1>
          <p className="text-gray-600">
            Clase: <span className="font-medium">{clase.titulo}</span>
          </p>
          {isEnrolledAsStudent() && (
            <div className="mt-2">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                <i className="fas fa-user-graduate mr-2"></i>
                Inscrito como estudiante en esta clase
              </Badge>
            </div>
          )}
        </div>
        
        {canCreateFormulario() && (
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="instituto-gradient btn-hover text-white" data-testid="create-formulario-button">
                  <i className="fas fa-plus mr-2"></i>
                  Nuevo Formulario
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Crear Nuevo Formulario de Evaluación</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateFormulario} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="titulo">Título *</Label>
                  <Input
                    id="titulo"
                    value={newFormulario.titulo}
                    onChange={(e) => setNewFormulario({ ...newFormulario, titulo: e.target.value })}
                    placeholder="Evaluación de Teología Bíblica"
                    required
                    data-testid="formulario-titulo-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción *</Label>
                  <Textarea
                    id="descripcion"
                    value={newFormulario.descripcion}
                    onChange={(e) => setNewFormulario({ ...newFormulario, descripcion: e.target.value })}
                    placeholder="Descripción del formulario de evaluación"
                    rows={3}
                    required
                    data-testid="formulario-descripcion-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="fecha_limite">Fecha Límite (opcional)</Label>
                  <Input
                    id="fecha_limite"
                    type="datetime-local"
                    value={newFormulario.fecha_limite}
                    onChange={(e) => setNewFormulario({ ...newFormulario, fecha_limite: e.target.value })}
                    data-testid="formulario-fecha-limite-input"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    disabled={creating}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="instituto-gradient text-white"
                    disabled={creating}
                    data-testid="formulario-submit-button"
                  >
                    {creating ? (
                      <>Creando... <i className="fas fa-spinner fa-spin ml-2"></i></>
                    ) : (
                      <>Crear Formulario <i className="fas fa-check ml-2"></i></>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={isGoogleDialogOpen} onOpenChange={setIsGoogleDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-hover" data-testid="import-google-form-button">
                <i className="fab fa-google mr-2 text-blue-500"></i>
                Importar de Google Classroom
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Importar Formulario de Google Classroom</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateGoogleFormulario} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <i className="fab fa-google text-blue-600 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-blue-800 mb-1">Importar desde Google Forms</h4>
                      <p className="text-sm text-blue-700">
                        Copia el enlace de tu formulario de Google Forms y completa los datos. 
                        Los estudiantes serán redirigidos a Google Forms para completar el formulario.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="google_titulo">Título *</Label>
                  <Input
                    id="google_titulo"
                    value={googleFormulario.titulo}
                    onChange={(e) => setGoogleFormulario({ ...googleFormulario, titulo: e.target.value })}
                    placeholder="Ej: Examen Parcial - Unidad 1"
                    required
                    data-testid="google-titulo-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="google_descripcion">Descripción *</Label>
                  <Textarea
                    id="google_descripcion"
                    value={googleFormulario.descripcion}
                    onChange={(e) => setGoogleFormulario({ ...googleFormulario, descripcion: e.target.value })}
                    placeholder="Describe brevemente este formulario de evaluación..."
                    rows={3}
                    required
                    data-testid="google-descripcion-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="google_form_url">URL de Google Forms *</Label>
                  <Input
                    id="google_form_url"
                    type="url"
                    value={googleFormulario.google_form_url}
                    onChange={(e) => setGoogleFormulario({ ...googleFormulario, google_form_url: e.target.value })}
                    placeholder="https://docs.google.com/forms/d/... o https://forms.gle/..."
                    required
                    data-testid="google-url-input"
                  />
                  <div className="text-xs text-gray-500">
                    <div className="flex items-center space-x-1 mb-1">
                      <i className="fas fa-info-circle text-blue-500"></i>
                      <span>Cómo obtener el enlace:</span>
                    </div>
                    <ol className="list-decimal list-inside space-y-1 ml-4">
                      <li>Ve a Google Forms y abre tu formulario</li>
                      <li>Haz clic en "Enviar" (botón morado en la esquina superior derecha)</li>
                      <li>Selecciona la pestaña de enlace (🔗)</li>
                      <li>Copia la URL y pégala aquí</li>
                    </ol>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="google_fecha_limite">Fecha Límite (opcional)</Label>
                  <Input
                    id="google_fecha_limite"
                    type="datetime-local"
                    value={googleFormulario.fecha_limite}
                    onChange={(e) => setGoogleFormulario({ ...googleFormulario, fecha_limite: e.target.value })}
                    data-testid="google-fecha-input"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsGoogleDialogOpen(false)}
                    disabled={creatingGoogle}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="instituto-gradient text-white"
                    disabled={creatingGoogle}
                    data-testid="google-submit-button"
                  >
                    {creatingGoogle ? (
                      <>Importando... <i className="fas fa-spinner fa-spin ml-2"></i></>
                    ) : (
                      <>Importar Formulario <i className="fab fa-google ml-2"></i></>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          {/* Edit Formulario Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Editar Formulario</DialogTitle>
              </DialogHeader>
              
              {editingFormulario && (
                <form onSubmit={handleEditFormulario} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-titulo">Título *</Label>
                    <Input
                      id="edit-titulo"
                      type="text"
                      value={editingFormulario.titulo}
                      onChange={(e) => setEditingFormulario({...editingFormulario, titulo: e.target.value})}
                      required
                      placeholder="Título del formulario"
                      data-testid="edit-formulario-titulo"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit-descripcion">Descripción</Label>
                    <Textarea
                      id="edit-descripcion"
                      value={editingFormulario.descripcion}
                      onChange={(e) => setEditingFormulario({...editingFormulario, descripcion: e.target.value})}
                      rows={3}
                      placeholder="Descripción del formulario"
                      data-testid="edit-formulario-descripcion"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="edit-fecha-limite">Fecha Límite</Label>
                    <Input
                      id="edit-fecha-limite"
                      type="datetime-local"
                      value={editingFormulario.fecha_limite}
                      onChange={(e) => setEditingFormulario({...editingFormulario, fecha_limite: e.target.value})}
                      data-testid="edit-formulario-fecha-limite"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      id="edit-activo"
                      type="checkbox"
                      checked={editingFormulario.activo}
                      onChange={(e) => setEditingFormulario({...editingFormulario, activo: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="edit-activo" className="text-sm">
                      Formulario activo
                    </Label>
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsEditDialogOpen(false)}
                      disabled={updating}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={updating}
                      className="instituto-gradient text-white"
                    >
                      {updating ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Actualizando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save mr-2"></i>
                          Guardar Cambios
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Formularios List */}
      {formularios.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {formularios.map((formulario) => (
            <Card key={formulario.id} className="card-hover shadow-lg border-0 overflow-hidden">
              <CardHeader className="pb-3 bg-gradient-to-r from-purple-50 to-indigo-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2 text-gray-900 line-clamp-2">
                      {formulario.titulo}
                    </CardTitle>
                    <p className="text-sm text-purple-600 font-medium">
                      <i className="fas fa-clipboard-list mr-2"></i>
                      Por: {formulario.profesor_nombre}
                    </p>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    {formulario.es_google_form && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        <i className="fab fa-google mr-1"></i>
                        Google Forms
                      </Badge>
                    )}
                    <Badge 
                      variant="secondary" 
                      className={formulario.activo ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}
                    >
                      <i className={`fas ${formulario.activo ? 'fa-check-circle' : 'fa-times-circle'} mr-1`}></i>
                      {formulario.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-6">
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {formulario.descripcion}
                </p>
                
                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-star mr-2 text-yellow-600"></i>
                    <span>Puntuación total: {formulario.puntuacion_total} pts</span>
                  </div>
                  
                  {formulario.fecha_limite && (
                    <div className="flex items-center text-sm text-gray-500">
                      <i className="fas fa-clock mr-2 text-orange-600"></i>
                      <span>Fecha límite: {formatDate(formulario.fecha_limite)}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-calendar-plus mr-2 text-blue-600"></i>
                    <span>Creado: {formatDate(formulario.fecha_creacion)}</span>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  {formulario.es_google_form ? (
                    /* Google Forms - Redirect to Google + Admin actions */
                    <div className="w-full space-y-2">
                      <Button
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white btn-hover"
                        onClick={() => window.open(formulario.google_form_url, '_blank')}
                        data-testid={`abrir-google-form-${formulario.id}`}
                      >
                        <i className="fab fa-google mr-2"></i>
                        Abrir en Google Forms
                      </Button>
                      
                      {/* Admin/Profesor can edit and delete Google Forms */}
                      {canEditFormulario(formulario) && (
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            className="flex-1 text-blue-600 border-blue-300 hover:bg-blue-50"
                            onClick={() => openEditDialog(formulario)}
                            data-testid={`editar-formulario-${formulario.id}`}
                          >
                            <i className="fas fa-edit mr-2"></i>
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleDeleteFormulario(formulario.id)}
                            data-testid={`eliminar-formulario-${formulario.id}`}
                          >
                            <i className="fas fa-trash mr-2"></i>
                            Eliminar
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Regular Forms */
                    getEffectiveRole() === 'estudiante' ? (
                      <Button
                        className="flex-1 instituto-gradient text-white btn-hover"
                        onClick={() => navigate(`/app/formulario/${formulario.id}/responder`)}
                        data-testid={`responder-formulario-${formulario.id}`}
                      >
                        <i className="fas fa-pen mr-2"></i>
                        Responder
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        {/* Primary action buttons */}
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            className="flex-1 btn-hover"
                            onClick={() => navigate(`/app/formulario/${formulario.id}/gestionar`)}
                            data-testid={`gestionar-formulario-${formulario.id}`}
                          >
                            <i className="fas fa-cogs mr-2"></i>
                            Gestionar
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1 btn-hover"
                            onClick={() => navigate(`/app/formulario/${formulario.id}/respuestas`)}
                            data-testid={`ver-respuestas-${formulario.id}`}
                          >
                            <i className="fas fa-chart-bar mr-2"></i>
                            Respuestas
                          </Button>
                        </div>
                        
                        {/* Edit and Delete buttons - only for form owners and admins */}
                        {canEditFormulario(formulario) && (
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                              onClick={() => openEditDialog(formulario)}
                              data-testid={`edit-formulario-${formulario.id}`}
                            >
                              <i className="fas fa-edit mr-2"></i>
                              Editar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                              onClick={() => handleDeleteFormulario(formulario)}
                              disabled={deleting}
                              data-testid={`delete-formulario-${formulario.id}`}
                            >
                              <i className={`fas ${deleting ? 'fa-spinner fa-spin' : 'fa-trash'} mr-2`}></i>
                              {deleting ? 'Eliminando...' : 'Eliminar'}
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="shadow-lg border-0">
          <CardContent className="text-center py-12">
            <i className="fas fa-clipboard-list text-gray-300 text-6xl mb-6"></i>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay formularios creados</h3>
            <p className="text-gray-600 mb-6">
              {['estudiante', 'pastor', 'graduado'].includes(user.rol)
                ? 'No hay formularios de evaluación disponibles para esta clase.'
                : 'Comienza creando formularios de evaluación para tus estudiantes.'}
            </p>
            {canCreateFormulario() && (
              <Button
                onClick={() => setIsDialogOpen(true)}
                className="instituto-gradient text-white btn-hover"
              >
                <i className="fas fa-plus mr-2"></i>
                Crear primer formulario
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Formularios;